import React, { useEffect, useState } from "react";

const AILoader = () => {
  const [dots, setDots] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDots((d) => (d + 1) % 4);
    }, 500);
    const progressInterval = setInterval(() => {
      setProgress((p) => (p >= 90 ? 90 : p + Math.random() * 8));
    }, 600);
    return () => {
      clearInterval(dotInterval);
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #f7faf5 0%, #ffffff 55%, #edf7f6 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>

      {/* Decorative background pattern - subtle shark marks */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.035, pointerEvents: "none" }} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <circle cx="30" cy="30" r="1.5" fill="#6fb64c" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* Top-left accent bar */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 4,
        height: "100%",
        background: "linear-gradient(180deg, #6fb64c 0%, #26a699 60%, transparent 100%)",
        opacity: 0.6,
      }} />

      {/* Top accent line */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: 3,
        background: "linear-gradient(90deg, #6fb64c 0%, #26a699 50%, transparent 100%)",
        opacity: 0.7,
      }} />

      {/* Brand label top-right */}
      <div style={{
        position: "absolute",
        top: 28,
        right: 36,
        display: "flex",
        alignItems: "center",
        gap: 8,
        opacity: 0.45,
      }}>
        <div style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "1.5px solid #6fb64c",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#6fb64c"/>
          </svg>
        </div>
        <span style={{ fontSize: 11, letterSpacing: "0.12em", color: "#545454", fontWeight: 500 }}>THE PRIME SHARK</span>
      </div>

      {/* Main card */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(16px)",
        borderRadius: 24,
        border: "1px solid rgba(111,182,76,0.15)",
        boxShadow: "0 8px 48px rgba(31,41,51,0.08), 0 2px 12px rgba(111,182,76,0.08)",
        padding: "52px 56px 44px",
        minWidth: 340,
        maxWidth: 400,
        position: "relative",
        overflow: "hidden",
      }}>

        {/* Card corner accent */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 60,
          height: 3,
          borderRadius: "0 0 4px 0",
          background: "linear-gradient(90deg, #6fb64c, #26a699)",
        }} />

        {/* Animated icon container */}
        <div style={{
          position: "relative",
          width: 120,
          height: 120,
          marginBottom: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {/* Outer spinning ring */}
          <div style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: "2px solid transparent",
            borderTopColor: "#6fb64c",
            borderRightColor: "rgba(111,182,76,0.3)",
            animation: "spin 1.8s linear infinite",
          }} />
          {/* Middle pulsing ring */}
          <div style={{
            position: "absolute",
            inset: 10,
            borderRadius: "50%",
            border: "1.5px solid transparent",
            borderBottomColor: "#26a699",
            borderLeftColor: "rgba(38,166,153,0.3)",
            animation: "spin-reverse 2.4s linear infinite",
          }} />
          {/* Glow circle */}
          <div style={{
            position: "absolute",
            inset: 20,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(111,182,76,0.12) 0%, rgba(38,166,153,0.06) 70%, transparent 100%)",
            animation: "pulse-glow 2s ease-in-out infinite",
          }} />
          {/* Center icon */}
          <div style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6fb64c 0%, #26a699 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 20px rgba(111,182,76,0.35)",
            animation: "float 3s ease-in-out infinite",
          }}>
            {/* Sparkles icon (matching ai-loader.png style) */}
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 4L17.2 8.8L22 10L17.2 11.2L16 16L14.8 11.2L10 10L14.8 8.8L16 4Z" fill="white" fillOpacity="0.95"/>
              <path d="M8 14L8.9 17.1L12 18L8.9 18.9L8 22L7.1 18.9L4 18L7.1 17.1L8 14Z" fill="white" fillOpacity="0.75"/>
              <path d="M21 16L21.7 18.3L24 19L21.7 19.7L21 22L20.3 19.7L18 19L20.3 18.3L21 16Z" fill="white" fillOpacity="0.55"/>
            </svg>
          </div>
        </div>

        {/* Text content */}
        <div style={{ textAlign: "center", marginBottom: 28, width: "100%" }}>
          <div style={{
            fontWeight: 700,
            fontSize: 22,
            color: "#1f2933",
            letterSpacing: "-0.01em",
            marginBottom: 8,
            lineHeight: 1.2,
          }}>
            Processing
            <span style={{ display: "inline-block", minWidth: 28, textAlign: "left", color: "#6fb64c" }}>
              {"...".slice(0, dots)}
            </span>
          </div>
          <div style={{
            color: "#7c7c7c",
            fontSize: 13.5,
            letterSpacing: "0.01em",
            lineHeight: 1.6,
            fontWeight: 400,
          }}>
            AI is analyzing your request.<br />
            <span style={{ color: "#26a699", fontWeight: 500 }}>Matching intelligence at work.</span>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ width: "100%", marginBottom: 8 }}>
          <div style={{
            height: 3,
            borderRadius: 999,
            background: "rgba(111,182,76,0.12)",
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #6fb64c 0%, #26a699 100%)",
              width: `${progress}%`,
              transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
              boxShadow: "0 0 8px rgba(111,182,76,0.5)",
            }} />
          </div>
        </div>

        {/* Status chips */}
        <div style={{
          display: "flex",
          gap: 8,
          marginTop: 16,
          flexWrap: "wrap",
          justifyContent: "center",
        }}>
          {["Verified Network", "AI Matching", "Secure"].map((label, i) => (
            <div key={label} style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              background: i === 1 ? "rgba(38,166,153,0.08)" : "rgba(111,182,76,0.08)",
              border: `1px solid ${i === 1 ? "rgba(38,166,153,0.2)" : "rgba(111,182,76,0.2)"}`,
              borderRadius: 999,
              padding: "4px 10px",
              animation: `chip-appear 0.4s ease ${i * 0.15}s both`,
            }}>
              <div style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: i === 1 ? "#26a699" : "#6fb64c",
                animation: "dot-blink 1.6s ease-in-out infinite",
                animationDelay: `${i * 0.3}s`,
              }} />
              <span style={{
                fontSize: 11,
                color: i === 1 ? "#26a699" : "#6fb64c",
                fontWeight: 600,
                letterSpacing: "0.04em",
              }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom tagline */}
      <div style={{
        marginTop: 32,
        fontSize: 12,
        color: "#7c7c7c",
        letterSpacing: "0.08em",
        opacity: 0.6,
        textTransform: "uppercase",
        fontWeight: 500,
      }}>
        Where Capital Meets Global Opportunity
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse {
          to { transform: rotate(-360deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.6; transform: scale(0.95); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes dot-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes chip-appear {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AILoader;